import React, { useState, useEffect } from 'react';
import { followAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

export const FollowButton = ({ questionId, variant = 'default' }) => {
  const { isAuthenticated } = useAuth();
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && questionId) {
      checkFollowStatus();
    }
  }, [isAuthenticated, questionId]);

  const checkFollowStatus = async () => {
    try {
      const response = await followAPI.checkFollow(questionId);
      setFollowing(response.data.following);
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to follow topics');
      return;
    }

    setLoading(true);
    try {
      const response = await followAPI.follow(questionId);
      setFollowing(response.data.following);
      toast.success(response.data.following 
        ? 'You will be notified of new contributions' 
        : 'Unfollowed topic'
      );
    } catch (error) {
      toast.error('Failed to update follow status');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  if (variant === 'icon') {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={handleFollow}
        disabled={loading}
        className={following ? 'text-orange-500' : 'text-slate-500'}
        title={following ? 'Unfollow' : 'Follow for updates'}
        data-testid="follow-button"
      >
        {following ? <Bell className="w-5 h-5 fill-current" /> : <BellOff className="w-5 h-5" />}
      </Button>
    );
  }

  return (
    <Button
      variant={following ? 'default' : 'outline'}
      onClick={handleFollow}
      disabled={loading}
      className={`gap-2 ${following ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
      data-testid="follow-button"
    >
      {following ? (
        <>
          <Bell className="w-4 h-4" />
          Following
        </>
      ) : (
        <>
          <BellOff className="w-4 h-4" />
          Follow
        </>
      )}
    </Button>
  );
};
